"use client";

import Link from "next/link";
import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";

import {
  browserConsentOptInRegion,
  browserEffectiveConsentChoices,
  browserPrivacySignal,
  readBrowserConsent,
  type ConsentSource,
  type SaveConsentOptions,
} from "@/src/features/consent/browserConsent";
import { usePublicFrontendTranslation } from "@smartmanager/shell-ui/shared/i18n/usePublicFrontendTranslation";
import { currentConsent, isDefaultAnalyticsBasis, type ConsentChoices } from "@/src/features/consent/policy";

/*
 * The tracker consent choices, drawn where they are asked for: in place of the
 * consent bar when someone presses Customize, and inline in Settings, Privacy.
 * Never a modal over the page. Category copy holds source strings, translated
 * where it renders.
 */

/** The consent bar's own surface; the panel itself draws no frame. */
export const CONSENT_SURFACE: CSSProperties = {
  borderRadius: 18,
  background: "linear-gradient(180deg, rgba(29,34,55,.96), rgba(22,26,44,.97))",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.12), inset 0 -8px 18px -14px rgba(0,212,255,.45), 0 24px 48px -18px rgba(0,0,0,.85), 0 0 0 1px rgba(255,255,255,.06)",
  backdropFilter: "blur(18px)",
};

/** The two equal answers share one style so neither is the "easy" one. */
export const EQUAL_CHOICE =
  "nv-btn flex-1 text-nv-text bg-white/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_4px_10px_-6px_rgba(0,0,0,0.6)] hover:bg-white/[0.10]";
export const QUIET_LINK =
  "inline-flex min-h-11 items-center px-1 text-[13px] font-semibold text-nv-act underline decoration-nv-act/35 underline-offset-4 hover:decoration-nv-act focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nv-act rounded-[6px]";

type Category = {
  key: keyof ConsentChoices;
  title: string;
  detail: string;
};

const CATEGORIES: readonly Category[] = [
  {
    key: "analytics",
    title: "Analytics",
    detail: "Counts which features get used and how pages load (PostHog, Vercel Analytics, Speed Insights). Never the content of your mail, files or conversations.",
  },
  {
    key: "diagnostics",
    title: "Diagnostics",
    detail: "Sends error reports and request timings from this device (Axiom), so we can find what broke. No message or file content.",
  },
];

export type ConsentPreferencesStart = {
  initial: ConsentChoices;
  /** The browser sends GPC / Do Not Track: every tracker stays off. */
  signal: boolean;
  /** No analytics decision yet and analytics is on by default here (outside the EU/UK). */
  defaultOn: boolean;
};

/**
 * Where the switches start: the choices in force, which is the stored choice,
 * else the regional default (analytics on outside the EU/UK). In the EU/UK a
 * legacy PostHog opt-in may pre-fill analytics, never save it.
 */
export function readConsentPreferencesStart(suggestAnalytics: boolean): ConsentPreferencesStart {
  const stored = currentConsent(readBrowserConsent());
  const optInRegion = browserConsentOptInRegion();
  const initial: ConsentChoices = stored || !optInRegion
    ? browserEffectiveConsentChoices(stored)
    : { analytics: suggestAnalytics, diagnostics: false };
  return {
    initial,
    signal: browserPrivacySignal(),
    defaultOn: !optInRegion && (!stored || isDefaultAnalyticsBasis(stored)),
  };
}

function Switch({ checked, onChange, labelledBy, describedBy, disabled = false }: {
  checked: boolean;
  onChange?: (next: boolean) => void;
  labelledBy: string;
  describedBy?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className="relative inline-flex h-11 w-[52px] shrink-0 items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nv-act"
    >
      <span
        aria-hidden="true"
        className="relative block h-[26px] w-[46px] rounded-full transition-colors motion-reduce:transition-none"
        style={{
          background: checked ? "rgba(0,212,255,.28)" : "rgba(0,0,0,.35)",
          boxShadow: checked
            ? "inset 0 0 0 1px rgba(0,212,255,.6), 0 0 14px -4px rgba(0,212,255,.8)"
            : "inset 0 1px 2px rgba(0,0,0,.6), inset 0 0 0 1px rgba(255,255,255,.06)",
        }}
      >
        <span
          className="absolute top-[3px] h-5 w-5 rounded-full transition-[left] motion-reduce:transition-none"
          style={{
            left: checked ? 23 : 3,
            background: checked ? "#00D4FF" : "#9AA3B8",
            boxShadow: "0 2px 6px rgba(0,0,0,.5)",
          }}
        />
      </span>
    </button>
  );
}

/**
 * The choices themselves. Opening it moves focus to its first control;
 * closing it returns focus to where it came from. Escape cancels.
 */
export function ConsentPreferencesPanel({
  start,
  onSave,
  onCancel,
  showTitle = true,
  className = "",
}: {
  start: ConsentPreferencesStart;
  onSave: (choices: ConsentChoices, source: ConsentSource, options?: SaveConsentOptions) => void;
  onCancel: () => void;
  /** Settings already titles the row the panel opens under. */
  showTitle?: boolean;
  className?: string;
}) {
  const t = usePublicFrontendTranslation();
  const { initial, signal, defaultOn } = start;
  const [choices, setChoices] = useState<ConsentChoices>(initial);
  // A switch left as the regional default showed it is not a decision: saving
  // it keeps following the default and never counts as consent elsewhere.
  const [analyticsTouched, setAnalyticsTouched] = useState(false);
  const titleId = useId();
  const baseId = useId();
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const returnFocus = document.activeElement;
    panelRef.current?.querySelector<HTMLElement>("button:not([disabled])")?.focus();
    return () => {
      if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") return;
    // Escape answers the panel only; the window or page it sits in stays.
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  };

  return (
    <section ref={panelRef} role="region" aria-labelledby={titleId} onKeyDown={onKeyDown} className={className} data-consent-preferences>
      <h2 id={titleId} className={showTitle ? "nv-title" : "sr-only"}>{t("Privacy choices")}</h2>
      <p className={showTitle ? "nv-meta mt-1.5" : "nv-meta"}>
        {t("Saved on this device, and to your account when you are signed in. Change them any time in Settings, Privacy.")}
      </p>

      {signal ? (
        <p className="nv-body mt-4 rounded-[12px] bg-nv-act/[0.08] px-3.5 py-3">
          {t("Your browser sends a Global Privacy Control or Do Not Track signal, so analytics and diagnostics stay off.")}
        </p>
      ) : defaultOn ? (
        <p className="nv-body mt-4 rounded-[12px] bg-nv-act/[0.08] px-3.5 py-3">
          {t("Analytics is on by default. Feature use and page speed only, never your content, never sold or used to train AI. Turn it off anytime.")}
        </p>
      ) : null}

      <div className="mt-4 divide-y divide-white/[0.06]">
        <div className="flex items-start gap-3 py-3">
          <div className="min-w-0 flex-1">
            <div id={`${baseId}-necessary`} className="nv-body font-semibold">{t("Necessary")}</div>
            <p id={`${baseId}-necessary-detail`} className="nv-meta mt-0.5">
              {t("Sign-in, security and remembering these choices. Always on.")}
            </p>
          </div>
          <Switch checked disabled labelledBy={`${baseId}-necessary`} describedBy={`${baseId}-necessary-detail`} />
        </div>
        {CATEGORIES.map((category) => (
          <div key={category.key} className="flex items-start gap-3 py-3">
            <div className="min-w-0 flex-1">
              <div id={`${baseId}-${category.key}`} className="nv-body font-semibold">{t(category.title)}</div>
              <p id={`${baseId}-${category.key}-detail`} className="nv-meta mt-0.5">{t(category.detail)}</p>
            </div>
            <Switch
              checked={!signal && choices[category.key]}
              disabled={signal}
              labelledBy={`${baseId}-${category.key}`}
              describedBy={`${baseId}-${category.key}-detail`}
              onChange={(next) => {
                if (category.key === "analytics") setAnalyticsTouched(true);
                setChoices((current) => ({ ...current, [category.key]: next }));
              }}
            />
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button type="button" className={EQUAL_CHOICE} onClick={() => onSave({ analytics: false, diagnostics: false }, "preferences")}>
          {t("Reject all")}
        </button>
        <button type="button" className={EQUAL_CHOICE} disabled={signal} onClick={() => onSave({ analytics: true, diagnostics: true }, "preferences")}>
          {t("Accept all")}
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <Link href="/privacy-policy" className={QUIET_LINK}>{t("Privacy Policy")}</Link>
        <div className="flex items-center gap-2">
          <button type="button" className="nv-btn" onClick={onCancel}>
            {t("Cancel")}
          </button>
          <button type="button" className="nv-btn nv-btn-primary" onClick={() => onSave(choices, "preferences", { analyticsAffirmed: analyticsTouched || !defaultOn })}>
            {t("Save choices")}
          </button>
        </div>
      </div>
    </section>
  );
}
